import { z } from 'zod'

/** POST /photo-frames レスポンス内 フレームレイヤーデータ（生 API スキーマ） */
export const PhotoFrameItemDataSchema = z.object({
  file: z.string().nullable(),
  credit: z.boolean(),
  magnification: z.number(),
  movable: z.boolean(),
  x: z.number().nullable(),
  y: z.number().nullable(),
})
export type PhotoFrameItemData = z.infer<typeof PhotoFrameItemDataSchema>

/** POST /photo-frames レスポンス内 エントリ（生 API スキーマ） */
export const PhotoFrameEntryDataSchema = z.object({
  id: z.number(),
  status: z.enum(['available', 'stamp_required', 'out_of_range']),
  message: z.string().nullable(),
  item: z.array(PhotoFrameItemDataSchema).nullable(),
})
export type PhotoFrameEntryData = z.infer<typeof PhotoFrameEntryDataSchema>

/** POST /photo-frames レスポンス（生 API スキーマ） */
export const PhotoFrameResponseSchema = z.object({
  photoFrames: z.array(PhotoFrameEntryDataSchema),
})
export type PhotoFrameResponse = z.infer<typeof PhotoFrameResponseSchema>

/** ArPhotoOverlay コンポーネントに渡すフレームデータ（DTO） */
export const PhotoFrameItemSchema = z.object({
  file: z.string().describe('フレーム画像 CloudFront URL'),
  fit: z.boolean(),
  magnification: z.number().describe('表示倍率（パーセンテージ: 100 = 等倍）'),
  credit: z.boolean(),
})
export type PhotoFrameItem = z.infer<typeof PhotoFrameItemSchema>

/**
 * POST /photo-frames レスポンスを ArPhotoOverlay 用 PhotoFrameItem[] に変換する。
 * status が 'available' かつ item が存在し file が non-null のものを変換する。
 * movable=false → fit=true（固定オーバーレイ）
 * magnification: v2 API は float（1.0 = 等倍、1.5 = 150% など）で返す。
 *   calculateCanvasDimensions はパーセンテージ表記（100 = 等倍）を期待するため * 100 で変換する。
 */
export const toPhotoFrameItems = (entries: PhotoFrameEntryData[]): PhotoFrameItem[] =>
  entries
    .filter((e) => e.status === 'available' && e.item != null)
    .flatMap((e) => e.item ?? [])
    .filter((item): item is PhotoFrameItemData & { file: string } => item.file !== null && item.file !== '')
    .map((item) => ({
      file: item.file,
      fit: !item.movable,
      magnification: item.magnification * 100,
      credit: item.credit,
    }))
